import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { Formik, Field, Form } from 'formik';
import { get, join } from 'lodash';
import * as Yup from 'yup';

import logError from 'app/lib/log-error';
import TicketDropdown from './ticket-dropdown';
import WORKFLOW_QUEUE_UNLOCK from '../queries/workflow-queue-unlock.graphql';
import UPDATE_WORKFLOW_QUEUE from '../queries/update-workflow-queue.graphql';
import WORKFLOW_QUEUE_DETAILS from '../queries/workflow-queue-details.graphql';
import WORKFLOW_QUEUE_OPTIONS from '../queries/workflow-queue-options.graphql';
import { withDeprecatedOption, dispositionsForCategory, categoriesForTicketType } from '../utils';
import useTicketCompleteValidator from './use-ticket-complete-validator';

const WORKFLOW_QUEUE_COMPLETED = 'UPDATEDSTATUS_COM';
const STANDARD_QUEUE_SPECIALTY_CODE = 'QUEUESPECIALTY_STANDARD';
const VPC_QUEUE_SPECIALTY_CODE = 'QUEUESPECIALTY_VPC';
const NURSE_ACTION_ACTOR_TYPE = 'NurseAction';

const validationSchema = Yup.object().shape({
  clinicalDesignation: Yup.string().required('You must select a clinical designation'),
  ticketType: Yup.string().required('You must select a ticket type'),
  category: Yup.string().required('You must select a category')
});

const TicketAssignment = ({ workflowQueueId, canUpdate }) => {
  const { loading: fetchDetailsLoading, error: fetchDetailsError, data: detailsData } = useQuery(
    WORKFLOW_QUEUE_DETAILS,
    {
      variables: { workflowQueueId }
    }
  );

  const { loading: fetchOptionsLoading, error: fetchOptionsError, data: optionsData } = useQuery(
    WORKFLOW_QUEUE_OPTIONS
  );

  const [unlockWorkflowQueue, { error: unlockWorkflowQueueError }] = useMutation(WORKFLOW_QUEUE_UNLOCK);
  const [
    updateWorkflowQueue,
    { error: updateWorkflowQueueError, loading: updateWorkflowQueueLoading, data: updateWorkflowQueueData }
  ] = useMutation(UPDATE_WORKFLOW_QUEUE);

  const updateError =
    get(updateWorkflowQueueError, 'message') ||
    join(get(updateWorkflowQueueData, 'workflowQueueUpdateMutation.errors'), ' ');

  useEffect(() => {
    const error = fetchDetailsError || fetchOptionsError || unlockWorkflowQueueError || updateError;

    if (error) {
      logError({ error }, 'WorkflowQueue State Machine Code Fail to load or update');
    }
  }, [fetchDetailsError, unlockWorkflowQueueError]);

  useTicketCompleteValidator(get(detailsData, ['workflowQueue']))

  if (fetchDetailsError || fetchOptionsError) {
    return (
      <div className="nurse-wfq-set-assignment update-message">
        Unable to fetch clinical designations, ticket types, categories and dispositions, please refresh.
      </div>
    );
  }

  if (fetchDetailsLoading || fetchOptionsLoading) {
    return <div className="nurse-wfq-set-assignment update-message">Loading...</div>;
  }

  const {
    workflowQueue: { stateMachineCode, queueSpecialtyCode, actorType, clinicalDesignation, ticketType, category, disposition }
  } = detailsData;

  const {
    workflowQueueOptions: { clinicalDesignations, ticketTypes, categoriesWithDispositions }
  } = optionsData;

  const wfqIsCompleted = stateMachineCode === WORKFLOW_QUEUE_COMPLETED;
  const ticketEditable =
    // It is a completed nurse queue ticket
    canUpdate ||
    // It is an open nurse or primary care nurse queue ticket
    (!wfqIsCompleted && (actorType === NURSE_ACTION_ACTOR_TYPE && [STANDARD_QUEUE_SPECIALTY_CODE, VPC_QUEUE_SPECIALTY_CODE].includes(queueSpecialtyCode)))

  const updateTicket = ({ clinicalDesignation, ticketType, category, disposition }) => {
    return updateWorkflowQueue({ variables: { clinicalDesignation, ticketType, category, disposition, workflowQueueId } });
  };

  return (
    <Formik
      initialValues={{
        clinicalDesignation: clinicalDesignation.code || '',
        ticketType: ticketType.code || '',
        category: category.code || '',
        disposition: disposition.code || ''
      }}
      validationSchema={validationSchema}
      onSubmit={updateTicket}
    >
      {({ values, setFieldValue, submitForm }) => {
        const updateTicketAndContinue = submissionValues =>
          submitForm(submissionValues).then(data => {
            if (data) {
              window.location.reload();
            }
          });

        const updateTicketAndUnassign = submissionValues =>
          submitForm(submissionValues).then(data => {
            if (data) {
              unlockWorkflowQueue({ variables: { workflowQueueId } }).then(() => {
                const queueSpecialtyCode = detailsData.workflowQueue.queueSpecialtyCode;

                window.location = `/workflow_queues/nurse?queue_specialty_cd=${
                  queueSpecialtyCode || STANDARD_QUEUE_SPECIALTY_CODE
                }`;
              });
            }
          });

        return (
          <React.Fragment>
            <Form className="nurse-wfq-set-assignment">
              <Field
                name="clinicalDesignation"
                label="Clinical Designation"
                options={withDeprecatedOption(clinicalDesignations, clinicalDesignation)}
                component={TicketDropdown}
                disabled={wfqIsCompleted}
              />
              <Field
                name="ticketType"
                label="Ticket Type"
                options={withDeprecatedOption(ticketTypes, ticketType)}
                component={TicketDropdown}
                disabled={wfqIsCompleted}
                onChange={() => setFieldValue('category', '')}
              />
              <Field
                name="category"
                label="Category"
                options={withDeprecatedOption(
                  categoriesForTicketType(categoriesWithDispositions, ticketTypes, values.ticketType),
                  values.category
                )}
                component={TicketDropdown}
                disabled={!ticketEditable}
                onChange={() => setFieldValue('disposition', '')}
              />
              <Field
                name="disposition"
                label="Disposition"
                options={withDeprecatedOption(
                  dispositionsForCategory(categoriesWithDispositions, values.category),
                  values.disposition
                )}
                component={TicketDropdown}
                disabled={!ticketEditable}
                />
              {ticketEditable && (
                <div>
                  <button
                    className="submit-link"
                    onClick={updateTicketAndContinue}
                    disabled={updateWorkflowQueueLoading}
                    type="button"
                  >
                    {updateWorkflowQueueLoading ? 'Loading...' : 'Save & Continue Working'}
                  </button>
                  {!wfqIsCompleted && (
                    <button
                      className="submit-link"
                      onClick={updateTicketAndUnassign}
                      disabled={updateWorkflowQueueLoading}
                      type="button"
                    >
                      {updateWorkflowQueueLoading ? 'Loading...' : 'Save & Go Back to Queue'}
                    </button>
                  )}
                </div>
              )}
            </Form>
            {updateError && <div className="update-message">Sorry, something went wrong. {updateError}</div>}
            {get(updateWorkflowQueueData, 'workflowQueueUpdateMutation.success') && (
              <div className="update-message">Ticket updated successfully.{updateError}</div>
            )}
          </React.Fragment>
        );
      }}
    </Formik>
  );
};

TicketAssignment.propTypes = {
  workflowQueueId: PropTypes.number
};

export default TicketAssignment;
